import React, {type ReactNode} from 'react';
import {Radio as BaseRadio} from 'reakit/Radio';
import {useSize} from '../../themes';
import {useRadioGroup} from '../RadioGroup';
import {RadioButton} from './RadioButton';
import {
  getDefinedValue,
  mergeRefs,
  ZERO_WIDTH_SPACE_CHARACTER,
} from '../../utils';
import {Text} from '../Text';

import {
  RadioWrapperView,
  RadioInputWrapperView,
  RadioInputView,
  RadioInputLabel,
} from './Radio.styles';

import type {
  HTMLAttributes,
  SizeContextSize,
  SpringTooling,
} from '../__SharedTypes';

export type RadioProps = {
  /**
   * Renders the style of the checked indicator.
   */
  checkedIndicator?: 'checkbox' | 'radio';
  /**
   * The color of the radio in its non-error state.
   */
  colorVariant?: 'blue' | 'purple' | 'green';
  /**
   * Renders the checked state for the Radio.
   */
  isChecked?: boolean;
  /**
   * Disables the `Radio`.
   */
  isDisabled?: boolean;
  /**
   * Renders the error state for the `Radio`.
   */
  isError?: boolean;
  /**
   * The name for all `Radio` components within a `RadioGroup`. The `name` should be the same for each `Radio` within a `RadioGroup`. If you apply the `name` prop to the `RadioGroup` component, it will be passed down to the `Radio` child components for you.
   */
  name?: string;
  /**
   * The label for the `Radio`.
   */
  label?: ReactNode;
  /**
   * An event handler for the onChange event
   */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * The size of the RadioButton.
   */
  size?: SizeContextSize;
  /**
   * The value for the `Radio`.
   */
  value?: string;
} & Omit<HTMLAttributes, 'onChange'> &
  SpringTooling;

/**
 * # Radio
 *
 * Radio is a form component that enables the user to select a single item
 * within a list of options.
 *
 * ## Usage
 *
 * ```jsx
 * import {RadioGroup, Radio} from '@packages/systems/spring';
 *
 * const Example = () => {
 *   const [value, setValue] = React.useState('crocus');
 *   return (
 *     <RadioGroup aria-label="Choose a flower" value={value} onChange={setValue}>
 *       <Radio label="Crocus" value="crocus" />
 *       <Radio label="Daffodils" value="daffodils" />
 *       <Radio label="Epimedium" value="epimedium" />
 *     </RadioGroup>
 *   );
 * };
 * ```
 *
 * @component
 */
export const Radio = React.forwardRef<HTMLElement, RadioProps>(
  (
    {
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      checkedIndicator = 'radio',
      colorVariant = 'blue',
      id,
      isChecked: isCheckedProp,
      isDisabled = false,
      isError: isErrorProp,
      label,
      name: nameProp,
      size: sizeProp,
      value,
      ...props
    }: RadioProps,
    ref
  ) => {
    const {isError: isErrorContext, name, radio} = useRadioGroup();
    const isError = getDefinedValue(isErrorProp, isErrorContext);
    const size = useSize(sizeProp);
    const isControlled =
      isCheckedProp != null || Object.keys(radio).includes('state');
    const [isChecked, setIsChecked] = React.useState(false);

    const reakitRadioProps = {
      checked: isControlled ? isCheckedProp : isChecked,
      onChange: () => setIsChecked(true),
      disabled: isDisabled,
      id,
      value,
    } as const;

    return (
      <BaseRadio {...radio} {...reakitRadioProps}>
        {/* @SPRING_CLEANING: See if we can better type radioProps here. This function-as-a-child pattern is coming from Reakit, so we could use whatever their types are for this function. */}
        {(radioProps: any) => {
          const mergedRefs = mergeRefs([radioProps.ref, ref]);
          const radioName = getDefinedValue(nameProp, name, radioProps.name);

          return (
            <RadioWrapperView
              align="start"
              isFullWidth={false}
              size={size}
              {...props}
            >
              <RadioInputWrapperView>
                <RadioInputView
                  {...radioProps}
                  aria-label={ariaLabel}
                  aria-labelledby={ariaLabelledBy}
                  name={radioName}
                  ref={mergedRefs}
                />
                <RadioButton
                  checkedIndicator={checkedIndicator}
                  colorVariant={colorVariant}
                  isChecked={radioProps.checked}
                  isDisabled={isDisabled}
                  isError={isError}
                  // shouldBypassAria: We are using the isChecked, isDisabled, and isIndeterminate props for styles,
                  // but we don't actually want to add `aria-checked`, `aria-disabled`, or `data-indeterminate` attributes
                  // to this <div> component. The RadioInputView component provides an actual <input type="radio" />
                  // element with all the necessary semantic markup in the HTML.
                  shouldBypassAria
                  size={size}
                />
                <TextAlignmentSpacer size={size} />
              </RadioInputWrapperView>
              {label ? (
                <RadioInputLabel
                  _shouldHideRequired
                  htmlFor={radioProps.id}
                  isDisabled={isDisabled}
                  shouldBypassAria
                >
                  {label}
                </RadioInputLabel>
              ) : null}
            </RadioWrapperView>
          );
        }}
      </BaseRadio>
    );
  }
);

Radio.displayName = 'Radio';
Radio.toString = RadioWrapperView.toString;

// @ts-expect-error - TS2552 - Cannot find name 'SizeContextSizeValue'. Did you mean 'SizeContextSize'?
const TextAlignmentSpacer = (props: {size: SizeContextSizeValue}) => (
  <Text {...props}>{ZERO_WIDTH_SPACE_CHARACTER}</Text>
);

export default Radio;
